import type { IDropdownButtonProps } from "@similarweb/ui-components";
import { translate } from "app/filters/i18nFilter";
import { sortBy } from "lodash";
import type { TechnologiesConditionEntry } from "app/pages/sales-intelligence/sub-modules/advanced-search/models/filters/technology/types";
import type { GroupedTechnologies } from "app/pages/sales-intelligence/modules/technology/types/technologies";
import { TechnologyDropdownTabType } from "app/pages/sales-intelligence/modules/technology/types/technologies";
import { entryToFilterTypeMap } from "app/pages/sales-intelligence/sub-modules/signals/components/filters/SignalTechnologiesFilter/utils/const";
import SignalFilterDropdownLabel from "app/pages/sales-intelligence/sub-modules/signals/components/filters/shared/SignalFilterDropdownLabel/SignalFilterDropdownLabel";
import useSignalFilterDropdownLabel from "app/pages/sales-intelligence/sub-modules/signals/hooks/useSignalFilterDropdownLabel";
import type { TranslatableNameWithType } from "app/pages/sales-intelligence/sub-modules/signals/models/services/FilterNamingService";
import { FilterType } from "app/pages/sales-intelligence/sub-modules/signals/models/services/FilterService";
import type TechnologySignalTechnologiesFilters from "app/pages/sales-intelligence/sub-modules/signals/models/technology/TechnologySignalTechnologiesFilters";
import filterService from "app/pages/sales-intelligence/sub-modules/signals/services/filters/filterService";
import { useCallback } from "react";

import type TechnologySignalFilters from "../../../models/technology/TechnologySignalFilters";
import FilterButtonLabel from "../shared/FilterDropdown/FilterButtonLabel/FilterButtonLabel";
import SignalFilterDropdownButton from "app/pages/sales-intelligence/sub-modules/signals/components/filters/shared/FilterDropdown/SignalFilterDropdownButton/SignalFilterDropdownButton";
import FilterTechnologiesDropdown from "./FilterTechnologiesDropdown/FilterTechnologiesDropdown";
import useCompoundTechnologiesFilterState from "./hooks/useCompoundTechnologiesFilterState";

type DropdownButtonProps = Pick<IDropdownButtonProps, "isOpen">;

export type SignalTechnologiesFilterProps = {
    filterKey: string;
    isValueEmpty: boolean;
    selectedValue: Partial<TechnologySignalTechnologiesFilters>;
    options: Readonly<GroupedTechnologies>;
    onValueChange(filters: Partial<TechnologySignalFilters>): void;
    onNoResultsFound(query: string): void;
};

/* Super-filter: manages three other filters */
const SignalTechnologiesFilter = (props: SignalTechnologiesFilterProps) => {
    const DEFAULT_ACTIVE_TAB = TechnologyDropdownTabType.Technologies;

    const { filterKey, isValueEmpty, selectedValue, options, onValueChange, onNoResultsFound } = props;

    const { selectedEntries, handleItemClick } = useCompoundTechnologiesFilterState(selectedValue, onValueChange);

    const buildFilterName = useCallback((): Readonly<TranslatableNameWithType> => {
        const nameBuilder = filterService.getFilterName(FilterType.Technologies);

        return nameBuilder.buildName(selectedValue, options);
    }, [options, selectedValue]);

    const { model: filterLabelModel } = useSignalFilterDropdownLabel({ buildName: buildFilterName });

    const handleOptionsSort = <T extends TechnologiesConditionEntry>(options: ReadonlyArray<T>): ReadonlyArray<T> => {
        return sortBy<T>(options, ({ type, name }) => {
            const filterKey = entryToFilterTypeMap[type];
            const optionsOfType = selectedValue[filterKey] ?? [];

            return !optionsOfType.includes(name);
        });
    };

    const renderButton = ({ isOpen }: DropdownButtonProps) => {
        return (
            <SignalFilterDropdownButton
                id={filterKey}
                isOpen={isOpen}
                hasValue={!isValueEmpty}
            >
                <FilterButtonLabel>
                    <SignalFilterDropdownLabel model={filterLabelModel} />
                </FilterButtonLabel>
            </SignalFilterDropdownButton>
        );
    };

    return (
        <FilterTechnologiesDropdown
            options={options}
            selectedEntries={selectedEntries}
            defaultActiveTab={DEFAULT_ACTIVE_TAB}
            buttonComponent={renderButton}
            searchPlaceholder={translate(`si.signals.filters.${filterKey}.search.placeholder`)}
            onNoResultsFound={onNoResultsFound}
            onItemsSort={handleOptionsSort}
            onSelect={handleItemClick}
        />
    );
};

export default SignalTechnologiesFilter;
